/**
 * useLayersPanelData
 *
 * Resolves all data + handlers the `LayersPanel` renders, for BOTH worlds:
 * - authenticated (`config` undefined): workspace-scoped layers + admin actions,
 * - public share (`config` provided): read-only, scoped to the share config.
 *
 * Keeps `LayersPanel` a thin presentational component; the per-mode branching
 * (which artifact/standard/custom rows, basemap count, whether to fetch) lives
 * here so the component just renders the returned view-model.
 *
 * @module components/layers/LayersPanel/hooks/useLayersPanelData
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { exportService } from '@/services/api/exportService';
import { useGeoMarkupStore } from '@/store';
import {
  selectIsAdmin,
  selectCustomLayers,
  selectCustomLayerVisibility,
  selectArtifactLayerVisibility,
  selectActiveWorkspaceCountryCode,
  selectActiveWorkspaceMapSettings,
  selectVisibleBasemapDefinitions,
} from '@/store/selectors';
import { getVisibleBasemaps } from '@/config/maplibre';
import { isLayerAllowedForCountry } from '@/config/mapFeatures';
import { isArtifactEnabled } from '@/config/features';
import { API_ENDPOINTS } from '@/constants/api';
import type { ArtifactVisibility } from '@/types/layers';
import type { PublicShareConfig } from '@/types/share';

import {
  ARTIFACT_LAYERS,
  STANDARD_LAYERS,
  VISIBLE_ARTIFACT_LAYERS,
} from '../../layerDefinitions';

export function useLayersPanelData(config?: PublicShareConfig) {
  const { t } = useTranslation();
  const isPublic = config !== undefined;

  // Panel open/close (shared flag the map toolbar toggles in both worlds).
  const isLayersExpanded = useGeoMarkupStore(
    (s) => s.ui.mapControls.isLayersExpanded
  );
  const closeLayersPanel = useGeoMarkupStore((s) => s.ui.closeLayersPanel);
  const isLayerConfiguratorOpen = useGeoMarkupStore(
    (s) => s.ui.layerConfigurator.isOpen
  );

  // Layer visibility + toggles (store-backed in both worlds).
  const artifactLayerVisibility = useGeoMarkupStore(
    selectArtifactLayerVisibility
  );
  const toggleArtifactLayer = useGeoMarkupStore(
    (s) => s.mapLayers.toggleArtifactLayer
  );
  const standardLayerVisibility = useGeoMarkupStore(
    useShallow((s) => s.mapLayers.standardLayerVisibility)
  );
  const toggleStandardLayer = useGeoMarkupStore(
    (s) => s.mapLayers.toggleStandardLayer
  );
  const customLayerVisibility = useGeoMarkupStore(selectCustomLayerVisibility);
  const toggleCustomLayerVisibility = useGeoMarkupStore(
    (s) => s.mapLayers.toggleCustomLayerVisibility
  );

  // Authenticated-only state (unused in public mode).
  const isAdmin = useGeoMarkupStore(selectIsAdmin);
  const countryCode = useGeoMarkupStore(selectActiveWorkspaceCountryCode);
  const mapSettings = useGeoMarkupStore(selectActiveWorkspaceMapSettings);
  const customLayers = useGeoMarkupStore(selectCustomLayers);
  const layersIsLoading = useGeoMarkupStore((s) => s.mapLayers.isLoading);
  const layersHasLoaded = useGeoMarkupStore((s) => s.mapLayers.hasLoaded);
  const deleteLayer = useGeoMarkupStore((s) => s.mapLayers.deleteLayer);
  const fetchLayers = useGeoMarkupStore((s) => s.mapLayers.fetchLayers);
  const openLayerConfigurator = useGeoMarkupStore(
    (s) => s.ui.openLayerConfigurator
  );
  const openLayerConfiguratorForEdit = useGeoMarkupStore(
    (s) => s.ui.openLayerConfiguratorForEdit
  );

  // Public-mode basemap allowlist (consumed by BasemapSwitcher via context).
  const visibleBasemaps = useGeoMarkupStore(
    useShallow(selectVisibleBasemapDefinitions)
  );

  // Artifact rows: all enabled (authed) or only the kinds the share includes.
  const artifactLayers = useMemo(() => {
    if (!config) return VISIBLE_ARTIFACT_LAYERS;
    const included: Record<keyof ArtifactVisibility, boolean> = {
      tasks: config.includeTasks,
      notes: config.includeNotes,
      obstructions: config.includeObstructions,
    };
    return ARTIFACT_LAYERS.filter(
      (layer) => included[layer.key] && isArtifactEnabled(layer.key)
    );
  }, [config]);

  // Standard rows: country-scoped (authed) or share `visibleLayers` (public).
  const standardLayers = useMemo(() => {
    if (config) {
      return STANDARD_LAYERS.filter((layer) =>
        config.visibleLayers.includes(layer.key)
      );
    }
    return STANDARD_LAYERS.filter((layer) =>
      isLayerAllowedForCountry(layer.key, countryCode)
    );
  }, [config, countryCode]);

  // Basemap group count must match what BasemapSwitcher renders.
  const visibleBasemapCount = useMemo(
    () =>
      config
        ? visibleBasemaps.length
        : getVisibleBasemaps(mapSettings, countryCode).length,
    [config, visibleBasemaps, mapSettings, countryCode]
  );

  const hasHiddenArtifactLayers = artifactLayers.some(
    (layer) => !artifactLayerVisibility[layer.key]
  );

  // Custom group: share layers (public) or workspace custom layers (authed).
  // The count feeds the group header; `showCustomGroup` hides the empty public
  // group while the authed group always shows (it owns its own empty/loading row).
  const customCount = config
    ? config.sharedCustomLayers.length
    : customLayers.length;
  const showCustomGroup = config ? config.sharedCustomLayers.length > 0 : true;

  // Fetch custom layers on open — authenticated only (public custom layers come
  // from the share config and are never fetched from the workspace API).
  useEffect(() => {
    if (!isPublic && isLayersExpanded && customLayers.length === 0) {
      fetchLayers();
    }
  }, [isPublic, isLayersExpanded, customLayers.length, fetchLayers]);

  const [isExporting, setIsExporting] = useState(false);

  const handleAddLayer = useCallback(() => {
    openLayerConfigurator();
  }, [openLayerConfigurator]);

  const handleExportGeoJson = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportService.downloadGeoJson();
    } finally {
      setIsExporting(false);
    }
  }, []);

  const handleEdit = useCallback(
    (layerId: string) => {
      openLayerConfiguratorForEdit(layerId);
    },
    [openLayerConfiguratorForEdit]
  );

  const handleDelete = useCallback(
    (layerId: string, layerName: string) => {
      if (
        window.confirm(t('layers.actions.confirmDelete', { name: layerName }))
      ) {
        deleteLayer(layerId);
      }
    },
    [deleteLayer, t]
  );

  const handleDownload = useCallback((layerId: string) => {
    window.open(API_ENDPOINTS.LAYERS.DOWNLOAD(layerId), '_blank');
  }, []);

  return {
    isPublic,
    isLayersExpanded,
    closeLayersPanel,
    isLayerConfiguratorOpen,
    isAdmin,
    visibleBasemapCount,
    artifactLayers,
    artifactLayerVisibility,
    toggleArtifactLayer,
    hasHiddenArtifactLayers,
    standardLayers,
    standardLayerVisibility,
    toggleStandardLayer,
    customLayers,
    customCount,
    showCustomGroup,
    customLayerVisibility,
    toggleCustomLayerVisibility,
    layersIsLoading,
    layersHasLoaded,
    isExporting,
    handleAddLayer,
    handleExportGeoJson,
    handleEdit,
    handleDelete,
    handleDownload,
  };
}
